/**
 * MEALFLOW - Form Validator Hook
 *
 * Hook Phoenix LiveView para validação de formulários em tempo real.
 *
 * Funcionalidades:
 * - Validação em tempo real (on blur, on change)
 * - Feedback visual instantâneo (valid/invalid states)
 * - Integração com ValidatedInput component
 * - Envio de estado de validação para LiveView
 * - Suporte para regras de validação customizadas
 * - Animações de erro/sucesso
 *
 * @example
 * // No template Phoenix:
 * <form phx-hook="FormValidator" data-validation-rules={@validation_rules}>
 *   <input name="code" data-validate="required|alpha_num|min:3" />
 *   <input name="email" data-validate="required|email" />
 * </form>
 *
 * @author MealFlow Team
 * @version 1.0.0
 */

export const FormValidator = {
  /**
   * Hook mounted - inicialização
   */
  mounted() {
    console.log('📋 [FormValidator] Hook mounted');

    this.validationState = new Map();
    this.validationRules = this.parseValidationRules();

    // Attach event listeners to all validatable inputs
    this.attachValidators();

    // Listen for LiveView updates (re-attach after re-render)
    this.handleEvent('revalidate_form', () => {
      this.validateAllFields();
    });
  },

  /**
   * Hook updated - re-attach após re-render do LiveView
   */
  updated() {
    console.log('📋 [FormValidator] Hook updated');
    this.attachValidators();
  },

  /**
   * Parse validation rules do atributo data-validation-rules
   */
  parseValidationRules() {
    const rulesAttr = this.el.getAttribute('data-validation-rules');
    if (!rulesAttr) return {};

    try {
      return JSON.parse(rulesAttr);
    } catch (e) {
      console.warn('⚠️ [FormValidator] Failed to parse validation rules:', e);
      return {};
    }
  },

  /**
   * Attach event listeners a todos os inputs com data-validate
   */
  attachValidators() {
    const inputs = this.el.querySelectorAll('[data-validate]');

    inputs.forEach((input) => {
      // Remove event listeners antigos (evitar duplicação)
      input.removeEventListener('blur', this.handleBlur);
      input.removeEventListener('input', this.handleInput);

      // Attach novos event listeners
      input.addEventListener('blur', this.handleBlur.bind(this));
      input.addEventListener('input', this.handleInput.bind(this));
    });

    console.log(`📋 [FormValidator] Attached validators to ${inputs.length} fields`);
  },

  /**
   * Handle blur event - validação completa ao sair do campo
   */
  handleBlur(event) {
    const input = event.target;
    this.validateField(input, true);
  },

  /**
   * Handle input event - validação em tempo real (debounced)
   */
  handleInput(event) {
    const input = event.target;

    // Clear existing timeout
    if (input.validationTimeout) {
      clearTimeout(input.validationTimeout);
    }

    // Debounce validation (300ms)
    input.validationTimeout = setTimeout(() => {
      this.validateField(input, false);
    }, 300);
  },

  /**
   * Valida um campo individual
   * @param {HTMLElement} input - Input element
   * @param {boolean} showErrors - Se deve mostrar erros visuais
   */
  validateField(input, showErrors = true) {
    const fieldName = input.name;
    const value = input.value;
    const rules = input.getAttribute('data-validate');

    if (!rules) return;

    const ruleList = rules.split('|');
    const errors = [];

    // Run each validation rule
    for (const rule of ruleList) {
      const [ruleName, ruleParam] = rule.split(':');
      const validator = this.validators[ruleName];

      if (validator) {
        const result = validator(value, ruleParam, input);
        if (result !== true) {
          errors.push(result);
        }
      } else {
        console.warn(`⚠️ [FormValidator] Unknown rule: ${ruleName}`);
      }
    }

    // Update validation state
    const isValid = errors.length === 0;
    this.validationState.set(fieldName, { valid: isValid, errors });

    // Apply visual feedback
    if (showErrors) {
      this.applyVisualFeedback(input, isValid, errors[0]);
    }

    // Notify LiveView of validation state
    this.pushEvent('field_validated', {
      field: fieldName,
      valid: isValid,
      errors: errors,
    });

    return isValid;
  },

  /**
   * Aplica feedback visual ao campo (CSS classes + mensagens)
   * @param {HTMLElement} input - Input element
   * @param {boolean} isValid - Se o campo é válido
   * @param {string|null} errorMessage - Mensagem de erro
   */
  applyVisualFeedback(input, isValid, errorMessage = null) {
    const container = input.closest('[data-validate-container]') || input.parentElement;

    // Remove classes antigas
    input.classList.remove('input-valid', 'input-invalid', 'animate-shake');

    // Apply new state
    if (input.value.length > 0) {
      if (isValid) {
        input.classList.add('input-valid');
        this.showSuccess(container, input);
      } else {
        input.classList.add('input-invalid', 'animate-shake');
        this.showError(container, input, errorMessage);
      }
    } else {
      // Campo vazio - neutral state
      this.clearFeedback(container);
    }
  },

  /**
   * Mostra indicador de sucesso
   */
  showSuccess(container, input) {
    this.clearFeedback(container);

    const errorDiv = container.querySelector('[data-error-message]');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }

    // Trigger success event (para componentes externos)
    input.dispatchEvent(new CustomEvent('validation:success', { bubbles: true }));
  },

  /**
   * Mostra mensagem de erro
   */
  showError(container, input, message) {
    this.clearFeedback(container);

    let errorDiv = container.querySelector('[data-error-message]');

    if (!errorDiv) {
      errorDiv = document.createElement('p');
      errorDiv.setAttribute('data-error-message', '');
      errorDiv.className = 'mt-1.5 text-xs text-red-600 flex items-center error-message';
      container.appendChild(errorDiv);
    }

    errorDiv.innerHTML = `
      <svg class="h-3.5 w-3.5 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>
      ${message}
    `;
    errorDiv.style.display = 'flex';

    // Trigger error event (para componentes externos)
    input.dispatchEvent(
      new CustomEvent('validation:error', { detail: { message }, bubbles: true })
    );
  },

  /**
   * Limpa feedback visual
   */
  clearFeedback(container) {
    const errorDiv = container.querySelector('[data-error-message]');
    if (errorDiv) {
      errorDiv.style.display = 'none';
    }
  },

  /**
   * Valida todos os campos do formulário
   * @returns {boolean} - true se todos os campos são válidos
   */
  validateAllFields() {
    const inputs = this.el.querySelectorAll('[data-validate]');
    let allValid = true;

    inputs.forEach((input) => {
      const isValid = this.validateField(input, true);
      if (!isValid) {
        allValid = false;
      }
    });

    console.log(`📋 [FormValidator] Validated ${inputs.length} fields. All valid: ${allValid}`);

    // Notify LiveView
    this.pushEvent('form_validated', { valid: allValid });

    return allValid;
  },

  /**
   * Validation rules - biblioteca de validadores
   */
  validators: {
    /**
     * Required - campo obrigatório
     */
    required(value) {
      return value && value.trim().length > 0 ? true : 'Este campo é obrigatório';
    },

    /**
     * Email - validação de email
     */
    email(value) {
      if (!value) return true; // Skip if empty (use 'required' for mandatory)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value) ? true : 'Email inválido';
    },

    /**
     * Min - comprimento mínimo
     */
    min(value, minLength) {
      if (!value) return true;
      const length = parseInt(minLength, 10);
      return value.length >= length ? true : `Mínimo de ${length} caracteres`;
    },

    /**
     * Max - comprimento máximo
     */
    max(value, maxLength) {
      if (!value) return true;
      const length = parseInt(maxLength, 10);
      return value.length <= length ? true : `Máximo de ${length} caracteres`;
    },

    /**
     * Alpha - apenas letras
     */
    alpha(value) {
      if (!value) return true;
      const alphaRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
      return alphaRegex.test(value) ? true : 'Apenas letras são permitidas';
    },

    /**
     * Alpha Numeric - letras e números
     */
    alpha_num(value) {
      if (!value) return true;
      const alphaNumRegex = /^[a-zA-Z0-9À-ÿ\s]+$/;
      return alphaNumRegex.test(value) ? true : 'Apenas letras e números são permitidos';
    },

    /**
     * Numeric - apenas números
     */
    numeric(value) {
      if (!value) return true;
      const numericRegex = /^[0-9]+$/;
      return numericRegex.test(value) ? true : 'Apenas números são permitidos';
    },

    /**
     * Phone - validação de telefone (PT)
     */
    phone(value) {
      if (!value) return true;
      const phoneRegex = /^(\+351)?[0-9]{9}$/;
      return phoneRegex.test(value.replace(/\s/g, ''))
        ? true
        : 'Número de telefone inválido';
    },

    /**
     * URL - validação de URL
     */
    url(value) {
      if (!value) return true;
      try {
        new URL(value);
        return true;
      } catch {
        return 'URL inválida';
      }
    },

    /**
     * Postal Code - validação de código postal (PT)
     */
    postal_code(value) {
      if (!value) return true;
      const postalRegex = /^[0-9]{4}-[0-9]{3}$/;
      return postalRegex.test(value) ? true : 'Código postal inválido (XXXX-XXX)';
    },

    /**
     * Match - campo deve corresponder a outro campo
     */
    match(value, fieldName, input) {
      if (!value) return true;
      const form = input.closest('form');
      const matchField = form.querySelector(`[name="${fieldName}"]`);

      if (!matchField) {
        console.warn(`⚠️ [FormValidator] Match field not found: ${fieldName}`);
        return true;
      }

      return value === matchField.value ? true : 'Os campos não correspondem';
    },
  },
};
